import FullCalendar from '@fullcalendar/react' // IMPORTANT! : needs to be imported first (before other @fullcalendar plugins), otherwise breaks

import BootstrapTheme from '@fullcalendar/bootstrap'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'
import hrLocale from '@fullcalendar/core/locales/hr'
import slLocale from '@fullcalendar/core/locales/sl'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import '@fullcalendar/react/dist/vdom'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import Select from 'react-select'
import { Button, Card, CardBody, Col, Container, Label, Row } from 'reactstrap'
import activities from '../../api/activities/hooks'
import agents from '../../api/agents/hooks'
import auth from '../../api/auth/hooks'
import { MODAL_TYPES, USER_ROLES } from '../../common/constants'
import { activityResponseFields } from '../../common/response/activity'
import AppPermissionsSwitcher from '../../Components/AppPermissionsSwitcher/AppPermissionsSwitcher'
import Loader from '../../Components/Common/Loader'
import PageTitle from '../../Components/Common/PageTitle'
import { formatAgentOptions } from '../../api/agents/formatters'
import { createActivityDateTime, setActivityModalData } from '../../Components/Modals/ActivityModal/utils'
import { showModal } from '../../store/modal'
import ExternalEvents from './ExternalEvents'

const hrLocaleModified = {
    ...hrLocale,
    buttonText: {
        ...hrLocale.buttonText,
        list: 'Lista',
    },
}

const availableCalendarLanguages = [
    { code: 'hr', local: hrLocaleModified },
    { code: 'de', local: deLocale },
    { code: 'en', local: enLocale },
    { code: 'sl', local: slLocale },
]

const ActivityCalendar = () => {
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()

    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({
            id: data.id,
            roles: data.roles,
            userSettings: data.userSettings,
        }),
    })
    const { data: agentsData } = agents.GetAgents.useQuery()
    const { mutate: updateAgentSettings } = agents.UpdateCurrentAgentSettings.useMutation()

    const agentOptions = formatAgentOptions(agentsData)

    const [dateRange, setDateRange] = useState(null)
    const [selectedAgents, setSelectedAgents] = useState(() => {
        if (currentAgent && currentAgent?.roles?.includes(USER_ROLES.ROLE_ADMIN)) {
            return [currentAgent.id?.toString()]
        }
        return []
    })
    const [selectedActivityTypes, setSelectedActivityTypes] = useState(
        currentAgent && currentAgent?.userSettings?.activityTypes ? currentAgent.userSettings.activityTypes : []
    )

    const selectedLocale = useMemo(() => {
        const localeObj = availableCalendarLanguages.find((loc) => loc.code === i18n.language)
        return localeObj?.local
    }, [i18n.language])

    const activityParams = useMemo(
        () => ({
            noLimit: 1,
            criteria: {
                rangeFrom: dateRange?.from,
                rangeTo: dateRange?.to,
                activityType: selectedActivityTypes,
                agent: selectedAgents,
            },
        }),
        [dateRange, selectedActivityTypes, selectedAgents]
    )

    const { data: activitiesData, isFetching: fetchingActivities } = activities.GetActivities.useQuery(activityParams, {
        enabled: !!dateRange,
    })

    const calendarEvents = useMemo(() => {
        return activitiesData?.items?.map((item) => ({
            ...item,
            // FullCalendar expects start & end values
            start: new Date(`${item.dateFrom} ${item.timeFrom}`),
            end: new Date(`${item.dateTo} ${item.timeTo}`),
            display: 'block',
            allDay: item.durationDay ? true : false,
            description: item.notes,
            textColor: '#273B4A',
            // 2e hex suffix ≈ 18% alpha - faded tint so the event title stays readable
            backgroundColor: `${item?.[activityResponseFields.ACTIVITY_TYPE]?.color}2e`,
        }))
    }, [activitiesData?.items])

    useEffect(() => {
        const externalEvents = document.getElementById('external-events')

        if (externalEvents) {
            new Draggable(document.getElementById('external-events'), {
                itemSelector: '.external-event',
            })
        }
    }, [])

    const handleDateSet = (arg) => {
        setDateRange({
            from: dayjs(arg.start).format('YYYY-MM-DD'),
            to: dayjs(arg.end).format('YYYY-MM-DD'),
        })
    }

    const openActivityModal = (eventData, mode) => {
        const payload = {
            modalType: MODAL_TYPES.ACTIVITY_MODAL,
            modalProps: {
                event: eventData,
                mode,
            },
        }

        dispatch(showModal(payload))
    }

    const handleActivityTypeChange = (newActivityTypes) => {
        if (currentAgent?.id) {
            updateAgentSettings({
                agentId: currentAgent.id,
                userSettings: {
                    ...currentAgent.userSettings,
                    activityTypes: newActivityTypes,
                },
            })
        }
    }

    // Handles click on "Add" button
    const handleCreateActivity = () => {
        const basicEvent = createActivityDateTime()
        openActivityModal(basicEvent, 'create')
    }

    // Handles click on calendar date (empty slot, event not added)
    const handleDateClick = (arg) => {
        const basicEvent = createActivityDateTime(arg.date)
        openActivityModal(basicEvent, 'create')
    }

    // Handles click on calendar displayed event (added activity)
    const handleEventClick = (arg) => {
        const event = arg.event
        const { extendedProps } = event

        const activityData = {
            id: event.id,
            title: event.title,
            ...extendedProps,
        }

        const formattedActivity = setActivityModalData(activityData)
        openActivityModal(formattedActivity, 'view')
    }

    // Handles drag n drop from activity categories sidebar to calendar date
    const onEventDrop = (event) => {
        const draggedEl = event.draggedEl
        const draggedElClass = draggedEl.className

        const isExternalEvent = draggedEl.classList.contains('external-event')
        const isNotExistingCalendarEvent = draggedElClass.indexOf('fc-event-draggable') === -1

        if (isExternalEvent && isNotExistingCalendarEvent) {
            const activityTypeId = draggedEl.getAttribute('data-activity-type-id')
            const activityTypeName = draggedEl.getAttribute('data-activity-type-name')

            const basicEvent = {
                ...createActivityDateTime(event.date),
                [activityResponseFields.TITLE]: activityTypeName,
                [activityResponseFields.ACTIVITY_TYPE]: { id: activityTypeId },
            }

            openActivityModal(basicEvent, 'create')
        }
    }

    const eventContent = ({ event }) => {
        const eventContact = event?.extendedProps?.contact
        const eventInfo = event?.extendedProps
        let contactName = ''

        if (eventContact) {
            if (eventContact.contactType === 'company') {
                contactName = eventContact.contactCompany?.name
            } else {
                contactName = `${eventContact.contactPerson?.firstName} ${eventContact.contactPerson?.lastName}`
            }
        }

        return (
            <>
                {eventContact && (
                    <div className="fs-13 fw-light" style={{ whiteSpace: 'normal' }}>
                        {contactName}
                    </div>
                )}

                <div className="fw-medium fs-13" style={{ whiteSpace: 'normal' }}>
                    {event?.title}
                </div>
                <div className="fw-light fs-13" style={{ whiteSpace: 'normal' }}>
                    {eventInfo?.activityType?.name}
                </div>
                {eventInfo?.activityAgent.map((agent) => {
                    return (
                        <div key={agent.id} className="fw-light fs-13" style={{ whiteSpace: 'normal' }}>
                            <span>
                                {agent.agent.firstName} {agent.agent.lastName}
                            </span>
                        </div>
                    )
                })}
            </>
        )
    }

    // Handles custom styles
    const eventDidMount = ({ event, view, el }) => {
        let eventType = view.type
        const newColor = event.extendedProps.activityType.color

        switch (eventType) {
            case 'dayGridDay': {
                const parentElement = el.parentElement
                parentElement.style.backgroundColor = newColor
                parentElement.style.marginBottom = '10px'
                break
            }
            case 'dayGridWeek':
            case 'dayGridMonth': {
                el.style.backgroundColor = newColor
                break
            }
            case 'listWeek': {
                el.style.backgroundColor = newColor
                el.style.color = 'black'
                break
            }
            default:
                break
        }
    }

    document.title = `${t('app.common.activities')} | Dimedia nekretnine CRM`

    return (
        <div className="page-content">
            <Container fluid>
                <PageTitle title={t('app.common.activities')} />
                <Row>
                    <Col xs={12}>
                        <Row>
                            <Col xl={3}>
                                {currentAgent && currentAgent?.roles?.includes(USER_ROLES.ROLE_ADMIN) && (
                                    <AppPermissionsSwitcher permission="AllowedAgents" module={'activities'} agentOptions={agentOptions}>
                                        {({ allowedAgentOptions }) => (
                                            <Card>
                                                <CardBody>
                                                    <Row>
                                                        <Col sm={12}>
                                                            <div className="d-flex justify-content-between">
                                                                <Label for="acitivity-agents-filter" id={`activity-agents-filterLbl`}>
                                                                    {t('activityCalendar.label')}
                                                                </Label>
                                                            </div>
                                                            <Select
                                                                onChange={(e) => {
                                                                    setSelectedAgents(e ? e.map((item) => item.value) : [])
                                                                }}
                                                                options={allowedAgentOptions}
                                                                isMulti={true}
                                                                name="activitiy-agents-filter"
                                                                id="activity-agents-filter"
                                                                value={allowedAgentOptions.filter(
                                                                    (option) => selectedAgents.indexOf(option.value) >= 0
                                                                )}
                                                                noOptionsMessage={() => t('form.common.select.noData')}
                                                                placeholder=""
                                                            />
                                                        </Col>
                                                    </Row>
                                                </CardBody>
                                            </Card>
                                        )}
                                    </AppPermissionsSwitcher>
                                )}
                                <Card className="card-h-100">
                                    <CardBody>
                                        <Button color="add" className="w-100" onClick={handleCreateActivity}>
                                            <i className="mdi mdi-plus"></i> {t('button.add')}
                                        </Button>

                                        <ExternalEvents
                                            selectedActivityTypes={selectedActivityTypes}
                                            setSelectedActivityTypes={setSelectedActivityTypes}
                                            handleActivityTypeChange={handleActivityTypeChange}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col xl={9}>
                                <Card className="card-h-100">
                                    <CardBody style={{ position: 'relative' }}>
                                        {fetchingActivities ? (
                                            <div className="activity-loader-container">
                                                <Loader />
                                            </div>
                                        ) : (
                                            ''
                                        )}

                                        <FullCalendar
                                            plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin, listPlugin]}
                                            initialView="dayGridMonth"
                                            slotDuration={'00:15:00'}
                                            handleWindowResize={true}
                                            themeSystem="bootstrap"
                                            headerToolbar={{
                                                left: 'prev,next today',
                                                center: 'title',
                                                right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
                                            }}
                                            locale={selectedLocale}
                                            events={calendarEvents}
                                            editable={true}
                                            droppable={true}
                                            selectable={true}
                                            dateClick={handleDateClick}
                                            eventClick={(args) => handleEventClick(args)}
                                            datesSet={handleDateSet}
                                            drop={onEventDrop}
                                            eventContent={eventContent}
                                            eventDidMount={eventDidMount}
                                            firstDay={1}
                                            fixedWeekCount={false}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default ActivityCalendar
